import {
  CAMPAIGN_LEVEL_COUNT,
  CAMPAIGN_VERSION,
  type CampaignLevel,
} from "../config/campaign";
import type { WeaponKind } from "../config/game";

export const PROGRESS_SCHEMA_VERSION = 2;
export const MAX_RECENT_RUNS = 5;

export interface LevelRecord {
  bestScore: number;
  stars: number;
  completions: number;
}

export interface RecentRun {
  id: string;
  mode: "quick" | "campaign";
  levelId: number | null;
  levelName: string;
  score: number;
  clogs: number;
  stars: number;
  weapon: WeaponKind;
  result: "victory" | "defeat";
  completedAt: string;
}

export interface PlayerProgress {
  schemaVersion: number;
  campaignVersion: number;
  nickname: string | null;
  createdAt: string;
  updatedAt: string;
  highestUnlockedLevel: number;
  levelRecords: Record<string, LevelRecord>;
  campaignClogs: number;
  lifetimeClogs: number;
  quickPlayBest: number;
  unlockedWeapons: WeaponKind[];
  selectedWeapon: WeaponKind;
  campaignComplete: boolean;
  cosmeticAccentUnlocked: boolean;
  failureStreaks: Record<string, number>;
  recentRuns: RecentRun[];
}

export interface RunRecordInput {
  mode: "quick" | "campaign";
  level: CampaignLevel | null;
  score: number;
  clogs: number;
  lives: number;
  weapon: WeaponKind;
  result: "victory" | "defeat";
  completedAt?: string;
}

export interface RunRecordResult {
  progress: PlayerProgress;
  stars: number;
  newWeapon: Exclude<WeaponKind, "poop"> | null;
  newBest: boolean;
}

const nowIso = (): string => new Date().toISOString();

export function createPlayerProgress(
  legacyBestScore = 0,
  now = nowIso(),
): PlayerProgress {
  return {
    schemaVersion: PROGRESS_SCHEMA_VERSION,
    campaignVersion: CAMPAIGN_VERSION,
    nickname: null,
    createdAt: now,
    updatedAt: now,
    highestUnlockedLevel: 1,
    levelRecords: {},
    campaignClogs: 0,
    lifetimeClogs: 0,
    quickPlayBest: Math.max(0, Math.floor(legacyBestScore)),
    unlockedWeapons: ["poop"],
    selectedWeapon: "poop",
    campaignComplete: false,
    cosmeticAccentUnlocked: false,
    failureStreaks: {},
    recentRuns: [],
  };
}

export function normalizeNickname(value: string): string | null {
  const normalized = value.replace(/\s+/g, " ").trim();
  const visibleLength = Array.from(normalized).length;
  if (
    visibleLength < 2 ||
    visibleLength > 16 ||
    /[\p{Cc}\p{Cf}<>]/u.test(normalized)
  )
    return null;
  return normalized;
}

export function weaponsForClogs(clogs: number): WeaponKind[] {
  return clogs >= 25
    ? ["poop", "bubble", "plunger"]
    : clogs >= 10
      ? ["poop", "bubble"]
      : ["poop"];
}

export function starsForRun(
  level: CampaignLevel,
  input: Pick<RunRecordInput, "result" | "lives" | "clogs" | "score">,
): number {
  if (input.result !== "victory") return 0;
  return (
    1 +
    (input.lives >= 2 ? 1 : 0) +
    (input.clogs >= level.clogTarget || input.score >= level.scoreTarget
      ? 1
      : 0)
  );
}

export function isLevelUnlocked(progress: PlayerProgress, id: number): boolean {
  return (
    id >= 1 && id <= CAMPAIGN_LEVEL_COUNT && id <= progress.highestUnlockedLevel
  );
}

export function completionPercent(progress: PlayerProgress): number {
  const completed = Object.values(progress.levelRecords).filter(
    (record) => record.completions > 0,
  ).length;
  return Math.round((completed / CAMPAIGN_LEVEL_COUNT) * 100);
}

export function comfyModeAvailable(
  progress: PlayerProgress,
  levelId: number,
): boolean {
  return (progress.failureStreaks[levelId.toString()] ?? 0) >= 2;
}

export function selectWeapon(
  progress: PlayerProgress,
  weapon: WeaponKind,
  now = nowIso(),
): PlayerProgress {
  if (!progress.unlockedWeapons.includes(weapon)) return progress;
  return { ...progress, selectedWeapon: weapon, updatedAt: now };
}

export function setNickname(
  progress: PlayerProgress,
  nickname: string | null,
  now = nowIso(),
): PlayerProgress {
  if (nickname === null) return { ...progress, nickname: null, updatedAt: now };
  const normalized = normalizeNickname(nickname);
  return normalized
    ? { ...progress, nickname: normalized, updatedAt: now }
    : progress;
}

export function recordRun(
  current: PlayerProgress,
  input: RunRecordInput,
): RunRecordResult {
  const completedAt = input.completedAt ?? nowIso();
  const next: PlayerProgress = {
    ...current,
    schemaVersion: PROGRESS_SCHEMA_VERSION,
    campaignVersion: CAMPAIGN_VERSION,
    lifetimeClogs: current.lifetimeClogs + Math.max(0, Math.floor(input.clogs)),
    updatedAt: completedAt,
  };
  let stars = 0;
  let newBest = false;
  let newWeapon: Exclude<WeaponKind, "poop"> | null = null;

  if (input.mode === "quick") {
    newBest = input.score > current.quickPlayBest;
    next.quickPlayBest = Math.max(
      current.quickPlayBest,
      Math.max(0, Math.floor(input.score)),
    );
  } else if (input.level) {
    const key = input.level.id.toString();
    const previous = current.levelRecords[key] ?? {
      bestScore: 0,
      stars: 0,
      completions: 0,
    };
    stars = starsForRun(input.level, input);
    newBest = input.score > previous.bestScore;
    next.campaignClogs =
      current.campaignClogs + Math.max(0, Math.floor(input.clogs));
    next.levelRecords = {
      ...current.levelRecords,
      [key]: {
        bestScore: Math.max(
          previous.bestScore,
          Math.max(0, Math.floor(input.score)),
        ),
        stars: Math.max(previous.stars, stars),
        completions:
          previous.completions + (input.result === "victory" ? 1 : 0),
      },
    };
    next.failureStreaks = {
      ...current.failureStreaks,
      [key]:
        input.result === "victory" ? 0 : (current.failureStreaks[key] ?? 0) + 1,
    };
    if (input.result === "victory") {
      next.highestUnlockedLevel = Math.min(
        CAMPAIGN_LEVEL_COUNT,
        Math.max(current.highestUnlockedLevel, input.level.id + 1),
      );
      if (input.level.id === CAMPAIGN_LEVEL_COUNT) next.campaignComplete = true;
      if (input.level.id >= 8) next.cosmeticAccentUnlocked = true;
    }
    const weapons = weaponsForClogs(next.campaignClogs);
    if (
      !current.unlockedWeapons.includes("bubble") &&
      weapons.includes("bubble")
    )
      newWeapon = "bubble";
    if (
      !current.unlockedWeapons.includes("plunger") &&
      weapons.includes("plunger")
    )
      newWeapon = "plunger";
    next.unlockedWeapons = weapons;
    if (!weapons.includes(next.selectedWeapon)) next.selectedWeapon = "poop";
  }

  const run: RecentRun = {
    id: `${completedAt}-${input.mode}-${input.level?.id ?? "quick"}`,
    mode: input.mode,
    levelId: input.level?.id ?? null,
    levelName: input.level?.name ?? "Quick Play",
    score: Math.max(0, Math.floor(input.score)),
    clogs: Math.max(0, Math.floor(input.clogs)),
    stars,
    weapon: input.weapon,
    result: input.result,
    completedAt,
  };
  next.recentRuns = [run, ...current.recentRuns].slice(0, MAX_RECENT_RUNS);
  return { progress: next, stars, newWeapon, newBest };
}
